import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  IconButton,
  CircularProgress,
  Alert,
  Button,
  Paper,
} from '@mui/material';
import {
  ArrowBack,
} from '@mui/icons-material';
import { fileSystemService, resolveApiBaseUrl } from '../services/api';
import { FileSystemNode } from '../types/api';

const ImageViewerPage: React.FC = () => {
  const { fileId } = useParams<{ fileId: string }>();
  const navigate = useNavigate();

  const [file, setFile] = useState<FileSystemNode | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [imageUrl, setImageUrl] = useState<string | null>(null);

  useEffect(() => {
    if (fileId) {
      loadFile(fileId);
    }
  }, [fileId]);

  const loadFile = async (id: string) => {
    setLoading(true);
    setError(null);
    try {
      const response = await fileSystemService.getFile(id);
      if (response.success && response.data) {
        setFile(response.data);
        // Set the image URL to fetch from the backend via the same-origin proxy
        setImageUrl(`${resolveApiBaseUrl()}/files/${id}/content`);
      } else {
        setError(response.error || 'Failed to load file');
      }
    } catch (err) {
      setError('Failed to load file');
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    // Check if there's a state parameter in the URL
    const params = new URLSearchParams(window.location.search);
    const stateParam = params.get('state');
    
    if (stateParam) {
      try {
        const state = JSON.parse(atob(stateParam));
        // If coming from backpack, navigate to home with state to restore backpack
        if (state.from === 'backpack') {
          // Navigate back to the originating page with state to restore the backpack modal.
          // replace: true removes the viewer entry from the history stack so that
          // pressing browser-back after returning cannot land back on this viewer page.
          navigate(state.basePath || '/today/log-habits', {
            replace: true,
            state: {
              openBackpack: true,
              expandedSections: state.expanded || [],
              nestedExpanded: state.nestedExpanded || [],
              expandedFolders: state.expandedFolders || [],
            }
          });
          return;
        }
      } catch (e) {
        console.error('Failed to parse state:', e);
      }
    }
    
    // Default behavior: just go back in history
    navigate(-1);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error && !file) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button onClick={handleBack} startIcon={<ArrowBack />}>
          Go Back
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column', bgcolor: 'background.default' }}>
      {/* Header */}
      <Paper 
        elevation={1} 
        sx={{ 
          p: 2, 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          borderRadius: 0,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconButton onClick={handleBack}>
            <ArrowBack />
          </IconButton>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {file?.name || 'Untitled'}
          </Typography>
        </Box>
      </Paper>

      {/* Image Display */}
      <Box 
        sx={{ 
          flex: 1, 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center',
          p: 3,
          overflow: 'auto',
          bgcolor: 'background.paper',
        }}
      >
        {imageUrl && (
          <img
            src={imageUrl}
            alt={file?.name || 'Image'}
            style={{
              maxWidth: '100%',
              maxHeight: '100%',
              objectFit: 'contain',
            }}
          />
        )}
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ m: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
    </Box>
  );
};

export default ImageViewerPage;
